import React from "react";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import { Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import Typography from "@limitless/modern/data-display/typography/Typography";
import { useTranslation } from "@limitless/common/hooks";
import Chip from "@limitless/modern/data-display/chip/Chip";
import { useExpertOnboardingContext } from "@limitless/expert";

import { useNavigation } from "@limitless/portal";
import { recordGroupName } from "@limitless/analytics";

import ArrowRight from "@limitless/icons/ArrowRight";
import ExpertDocumentVerificationOverview from "./ExpertDocumentVerificationOverview";
import {
    extraInfoDetails,
    getMemberDetailsStatus,
    getPracticeQuestionStatus,
    isDocumentVerificationRequired,
    isRedChip,
    shouldPracticeTaskVisible
} from "@limitless/expert/utils/OnboardingStatsHelper";

const useStyles = makeStyles(theme => ({
    forwardIcon: {
        fontSize: "1rem",
        marginLeft: 8,
        marginBottom: 1
    },
    cta: {
        marginLeft: "auto"
    },
    row1Text: {
        display: "flex",
        alignItems: "center",
        color: theme.palette.grey[4]
    },
    row2Text: {
        display: "flex",
        alignItems: "center",
        color: theme.palette.primary.main
    },
    chip: {
        backgroundColor: theme.palette.secondary.light,
        minWidth: 57,
        borderRadius: "8px",
        marginLeft: theme.spacing(1),
        whiteSpace: "nowrap"
    },
    chipRed: {
        backgroundColor: theme.palette.error.light,
        minWidth: 57,
        borderRadius: "8px",
        marginLeft: theme.spacing(1),
        whiteSpace: "nowrap"
    },
    labelBox: {
        display: "flex",
        alignItems: "center",
        maxWidth: 180
    },
    icon: {
        marginLeft: theme.spacing(1),
        color: theme.palette.primary.main,
        fontSize: "16px !important"
    },
    itemGrid: {
        display: "flex",
        alignItems: "center",
        maxWidth: "100%"
    }
}));

const ExpertPracticeGroupCTA = ({ practiceGroup, isIdentityVerified, isDocumentVerified, isPaypalVerified }) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const { onboardingDetail, getOnboardingDetail } = useExpertOnboardingContext();
    const { navigate } = useNavigation();

    const CTAData = [
        {
            label: "Practice Tasks:",
            value: getPracticeQuestionStatus(practiceGroup),
            shouldVisible: shouldPracticeTaskVisible(practiceGroup),
            row: 1,
            sx: { color: theme => theme.palette.grey[4] }
        },
        {
            label: "Additional Details",
            value: getMemberDetailsStatus(practiceGroup),
            sx: { ...(shouldPracticeTaskVisible(practiceGroup) && { ml: 2 }) },
            shouldVisible: !!extraInfoDetails(practiceGroup),
            row: 1
        },
        {
            label: "ID Verification",
            value: `${!isIdentityVerified && "to do"}`,
            icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />,
            shouldVisible: !isIdentityVerified,
            navigateTo: "/onboarding/detail?tab=idVerificationTab",
            row: 2
        },
        {
            label: "Document Verification",
            value: `${isDocumentVerificationRequired(practiceGroup, isDocumentVerified) && "to do"}`,
            icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />,
            sx: { ml: 2 },
            component: <ExpertDocumentVerificationOverview onboardingDetail={onboardingDetail} />,
            shouldVisible: isDocumentVerificationRequired(practiceGroup, isDocumentVerified),
            navigateTo: "/onboarding/detail?tab=documentVerificationTab",
            row: 2
        },
        {
            label: "Payment Details",
            icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />,
            value: `${!isPaypalVerified && "to do"}`,
            sx: { ml: 2 },
            shouldVisible: !isPaypalVerified,
            navigateTo: "/settings?tab=payment",
            row: 2
        }
    ];

    return (
        <Box marginTop={"-14px"}>
            <Grid container alignItems="center" columnGap={1} pt={1}>
                {CTAData.filter(item => item.row === 1).map(
                    (item, index) =>
                        item.shouldVisible && (
                            <React.Fragment key={index}>
                                <Grid item sx={item.sx || {}}>
                                    <Typography variant="body2" className={classes.row1Text}>
                                        {item.label}
                                    </Typography>
                                </Grid>
                                <Chip
                                    className={isRedChip(item?.value) ? classes.chipRed : classes.chip}
                                    label={t(item?.value) || ""}
                                />
                            </React.Fragment>
                        )
                )}
            </Grid>
            <Grid container alignItems="center" columnGap={1} pt={1}>
                {CTAData.filter(item => item.row === 2).map(
                    (item, index) =>
                        item.shouldVisible && (
                            <Grid
                                item
                                key={index}
                                className={classes.itemGrid}
                                onClick={() => {
                                    if (item.navigateTo) {
                                        getOnboardingDetail(practiceGroup);
                                        navigate(item.navigateTo);
                                        recordGroupName(practiceGroup.groupName);
                                    }
                                }}
                                style={{ cursor: item.navigateTo ? "pointer" : "default" }}
                            >
                                <Typography variant="body2" className={classes.row2Text}>
                                    <Box className={classes.labelBox}>{item.label}</Box>
                                    {item.icon &&
                                        React.cloneElement(item.icon, {
                                            className: classes.icon
                                        })}
                                </Typography>
                                <Chip
                                    className={isRedChip(item?.value) ? classes.chipRed : classes.chip}
                                    label={item.value}
                                />
                            </Grid>
                        )
                )}
            </Grid>
        </Box>
    );
};

export default ExpertPracticeGroupCTA;

ExpertPracticeGroupCTA.propTypes = {
    practiceGroup: PropTypes.object.isRequired,
    type: PropTypes.string,
    isIdentityVerified: PropTypes.bool,
    isDocumentVerified: PropTypes.bool,
    isPaypalVerified: PropTypes.string,
    index: PropTypes.number.isRequired
};
